import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import Button from "../button";
import Input from "../input";
import Modal from "../modal";
import Select from "../select";
import Textarea from "../textarea";
import ConfirmationModal from "../confirmation-modal";
import { activeCampaignSubmit } from "../../lib/forms";

// Active Campaign field names.
const fieldMap = {
  companyName: "customer_account",
  location: "field[8]",
  membershipType: "field[107]",
  details: "field[9]",
  subscribe: "field[24][]",
};

const membershipChoices = [
  "Mailbox",
  "Social Membership",
  "Club Membership",
  "Dedicated Desk",
  "2 Person Office",
  "Standard Office",
  "Office Suite",
  "Premium Office",
  "Undecided",
];

const MembershipFormModal = ({ locations, ...props }) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "2",
      f: "2",
      s: "0",
      c: "0",
      m: "0",
      act: "sub",
      v: "2",
      or: "d8e581f8c366bb5e4ac5adb4aed5fa8f",
    },
  });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "membership",
        location: data[fieldMap.location],
        firstname: data.firstname,
        lastname: data.lastname,
        phone: data.phone,
        email: data.email,
      },
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };
  const onError = (e) => console.log(e);

  const { errors } = formState;

  if (submitted) {
    return <ConfirmationModal {...props} />;
  }

  if (error) {
    return (
      <Modal {...props}>
        <>
          <h2 className="font-bold text-2xl text-center">
            Something went wrong!
          </h2>

          <p className="text-md my-4 text-center font-light">
            {error?.message}
          </p>
        </>
      </Modal>
    );
  }

  return (
    <Modal {...props}>
      <form className={clsx("p-6")} onSubmit={handleSubmit(onSubmit, onError)}>
        <h2 className="font-bold text-3xl text-center">
          Interested in a Membership?
        </h2>

        <div className="my-4">
          <div className="form-group-inline">
            <Input
              type="text"
              placeholder="First name *"
              {...register("firstname", {
                required: "First name is required",
              })}
              errorText={errors.firstname && errors.firstname.message}
              inline
            />
            <Input
              type="text"
              placeholder="Last name *"
              {...register("lastname", { required: "Last name is required" })}
              errorText={errors.lastname && errors.lastname.message}
              inline
            />
          </div>

          <Input
            type="text"
            placeholder="Your email address *"
            {...register("email", { required: "Email is required" })}
            errorText={errors.email && errors.email.message}
          />

          <Input
            type="text"
            placeholder="Phone number *"
            {...register("phone", {
              required: "Phone number is required",
            })}
            errorText={errors.phone && errors.phone.message}
          />
          <Input
            type="text"
            placeholder="Company name *"
            {...register(fieldMap.companyName, {
              required: "Company name is required",
            })}
            errorText={
              errors[fieldMap.companyName] &&
              errors[fieldMap.companyName].message
            }
          />

          <Select
            {...register(fieldMap.membershipType, {
              required: "Membership is required",
            })}
            errorText={
              errors[fieldMap.membershipType] &&
              errors[fieldMap.membershipType].message
            }
          >
            <option value="" disabled selected>
              Which membership are you interested in? *
            </option>
            {membershipChoices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </Select>

          <Select
            {...register(fieldMap.location, {
              required: "Location is required",
            })}
            errorText={
              errors[fieldMap.location] && errors[fieldMap.location].message
            }
          >
            <option value="" disabled selected>
              Which location are you interested in? *
            </option>
            {locations.map((location) => (
              <option
                value={`${location.address1}, ${location.city} ${location.province}`}
                key={location.slug}
              >
                {location.address1}, {location.city} {location.province}
              </option>
            ))}
          </Select>

          <Textarea
            type="text"
            placeholder="Please share any additional information"
            rows="3"
            {...register(fieldMap.details)}
          />
        </div>

        <Button type="submit" className="w-full my-4" outline>
          Submit
        </Button>
      </form>
    </Modal>
  );
};

export default MembershipFormModal;
